import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Trophy, Lightning } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import BriefingScreen from "../components/market/BriefingScreen";
import CardModeGame from "../components/market/CardModeGame";
import ReviewScreen from "../components/market/ReviewScreen";
import { DECISION_META, ORDER } from "../components/market/decisionMeta";
import {
    getChallenge,
    submitMarketDecisions,
    getMyMarketDecisions,
    getCommunityDecisions,
    getChallengeLeaderboard,
    getChallengeSummary,
    getUserArchetype,
    buildShareCardUrl,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function MarketChallenge() {
    const { cid } = useParams();
    const { user } = useAuth();
    const [challenge, setChallenge] = useState(null);
    const [picks, setPicks] = useState({});
    const [confidences, setConfidences] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [community, setCommunity] = useState(null);
    const [resolution, setResolution] = useState(null);
    const [summary, setSummary] = useState(null);
    const [guestUserId, setGuestUserId] = useState(() => localStorage.getItem(`td_guest_uid_${cid}`) || null);
    // Card-mode game state:
    //   - "briefing"  → scoring rules screen, click Start to enter cards
    //   - "playing"   → swipe-style one-card-at-a-time UI
    //   - "review"    → all 10 picks recap, change or Lock-in
    //   - "grid"      → power-user fallback (old grid mode)
    const [gamePhase, setGamePhase] = useState("briefing");
    const [cardIndex, setCardIndex] = useState(0);
    const [shuffleSeed] = useState(() => Math.random());

    const refreshCommunity = (id) => {
        getCommunityDecisions(id)
            .then((data) => setCommunity(data))
            .catch(() => {});
    };

    const loadMyAttempt = (id, guid) => {
        getMyMarketDecisions(id, guid || null)
            .then((d) => {
                if (!d?.submitted) return;
                setSubmitted(true);
                setPicks(d.attempt?.decisions || {});
                setResolution(d.attempt?.resolution || null);
                setGamePhase("done");
            })
            .catch(() => {});
    };

    useEffect(() => {
        let cancelled = false;
        getChallenge(cid)
            .then((c) => { if (!cancelled) setChallenge(c); })
            .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
        // Load attempt: authed user OR guest with stored user_id.
        if (user) {
            loadMyAttempt(cid, null);
        } else if (guestUserId) {
            loadMyAttempt(cid, guestUserId);
        }
        refreshCommunity(cid);
        getChallengeSummary(cid).then((s) => {
            if (s?.available) setSummary(s.summary);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [cid, user?.user_id, guestUserId]);

    const counts = useMemo(() => {
        const out = { BUY: 0, KEEP: 0, AVOID: 0, SELL: 0 };
        for (const v of Object.values(picks)) if (out[v] != null) out[v] += 1;
        return out;
    }, [picks]);

    const constraintsMessage = useMemo(() => {
        if (!challenge) return null;
        const c = challenge.constraints || {};
        const errs = [];
        for (const d of ORDER) {
            const min = c[`min${d[0]}${d.slice(1).toLowerCase()}`];
            const max = c[`max${d[0]}${d.slice(1).toLowerCase()}`];
            if (min != null && counts[d] < min) errs.push(`${d}: need ${min}, got ${counts[d]}`);
            if (max != null && counts[d] > max) errs.push(`${d}: max ${max}, got ${counts[d]}`);
        }
        const totalAssigned = Object.values(picks).filter(Boolean).length;
        const missing = (challenge.players?.length || 0) - totalAssigned;
        if (missing > 0) errs.push(`${missing} player${missing === 1 ? "" : "s"} not yet decided`);
        return errs;
    }, [challenge, counts, picks]);

    const [guestName, setGuestName] = useState(() => localStorage.getItem("td_guest_name") || "");
    const [guestEmail, setGuestEmail] = useState(() => localStorage.getItem("td_guest_email") || "");

    const handlePick = (pid, decision) => {
        if (submitted) return;
        setPicks((prev) => ({ ...prev, [String(pid)]: decision }));
    };

    const handleSubmit = async () => {
        if (constraintsMessage && constraintsMessage.length > 0) {
            toast.error(constraintsMessage[0]);
            return;
        }
        if (!user) {
            const name = guestName.trim();
            if (name.length < 2) {
                toast.error("Enter a display name (min. 2 chars) so you appear on the leaderboard.");
                return;
            }
            if (guestEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guestEmail)) {
                toast.error("Email format invalid — leave blank to skip.");
                return;
            }
        }
        setSubmitting(true);
        try {
            const opts = {
                confidences,
                ...(!user ? { displayName: guestName.trim(), email: guestEmail.trim() || null } : {}),
            };
            const res = await submitMarketDecisions(cid, picks, opts);
            if (!user) {
                localStorage.setItem("td_guest_name", guestName.trim());
                if (guestEmail) localStorage.setItem("td_guest_email", guestEmail.trim());
                if (res.user?.user_id) {
                    localStorage.setItem(`td_guest_uid_${cid}`, res.user.user_id);
                    setGuestUserId(res.user.user_id);
                }
            }
            setSubmitted(true);
            toast.success(`Locked in as ${res.user?.name || "you"}! ${res.counts.BUY}× BUY · ${res.counts.KEEP}× KEEP · ${res.counts.AVOID}× AVOID · ${res.counts.SELL}× SELL`);
            refreshCommunity(cid);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submit failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (error === "not_found") {
        return <AppShell><div className="max-w-3xl mx-auto py-20 text-center text-white/60">Challenge not found.</div></AppShell>;
    }
    if (!challenge) {
        return <AppShell><div className="max-w-3xl mx-auto py-20 text-center text-white/60 font-mono text-sm">Loading challenge…</div></AppShell>;
    }
    if (!["MARKET_DECISION", "WC_MATCH_CALL"].includes(challenge.challengeType)) {
        return <AppShell><div className="max-w-3xl mx-auto py-20 text-center text-white/60">This page only renders MARKET_DECISION / WC_MATCH_CALL challenges.</div></AppShell>;
    }

    const dur = challenge.duration || {};
    const isTransferDesk = challenge.mode === "TRANSFER_DESK";
    const constraints = challenge.constraints || {};
    const status = challenge.status;
    const isLocked = status === "locked";
    const isEvaluating = status === "evaluating";
    const isEvaluated = status === "evaluated";

    // Stable shuffled player order — re-uses same order across renders.
    const shuffledPlayers = (challenge.players || []).slice().sort((a, b) => {
        const ha = (String(a.playerId) + shuffleSeed).split("").reduce((x, c) => x + c.charCodeAt(0), 0);
        const hb = (String(b.playerId) + shuffleSeed).split("").reduce((x, c) => x + c.charCodeAt(0), 0);
        return ha - hb;
    });
    const totalCards = shuffledPlayers.length;
    const currentPlayer = shuffledPlayers[cardIndex];
    const inGame = !submitted && !isLocked && !isEvaluating;
    const showBriefing = inGame && gamePhase === "briefing";
    const showPlaying = inGame && gamePhase === "playing" && currentPlayer;
    const showReview = inGame && gamePhase === "review";
    const showGrid = inGame && gamePhase === "grid";

    const goToCard = (idx) => {
        setCardIndex(Math.max(0, Math.min(totalCards - 1, idx)));
    };

    const handleCardPick = (decision) => {
        if (!currentPlayer) return;
        setPicks((prev) => ({ ...prev, [String(currentPlayer.playerId)]: decision }));
        // Auto-advance after a tiny delay so the user sees the choice register.
        setTimeout(() => {
            if (cardIndex + 1 >= totalCards) {
                setGamePhase("review");
            } else {
                setCardIndex(cardIndex + 1);
            }
        }, 220);
    };

    const handleCardConfidence = (pid, value) => {
        setConfidences((prev) => ({ ...prev, [String(pid)]: value }));
    };

    return (
        <AppShell>
            <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="market-challenge-page">
                {/* Lifecycle banner */}
                {(isLocked || isEvaluating) && (
                    <section
                        className={`border ${isEvaluating ? "border-fa-mint/40 bg-fa-mint/5" : "border-fa-yellow/40 bg-fa-yellow/5"} p-4 flex items-center gap-3`}
                        data-testid={isEvaluating ? "status-banner-evaluating" : "status-banner-locked"}
                    >
                        <div className={`w-2 h-2 rounded-full ${isEvaluating ? "bg-fa-mint animate-pulse" : "bg-fa-yellow"}`} />
                        <div className="flex-1">
                            <div className={`font-mono text-[10px] uppercase tracking-widest ${isEvaluating ? "text-fa-mint" : "text-fa-yellow"}`}>
                                {isEvaluating ? "Grading in progress" : "Deadline passed · locked"}
                            </div>
                            <div className="font-mono text-[11px] text-white/60 mt-0.5">
                                {isEvaluating
                                    ? "Resolver is scoring your picks. Check back in ~5min for the leaderboard and Beat-the-Model breakdown."
                                    : "Submissions are closed. Grading will start automatically once the gameweek finishes."}
                            </div>
                        </div>
                    </section>
                )}

                {/* Header */}
                <header className="border border-fa-violet/30 bg-[#0d0d0d] p-5">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-2">
                        <Lightning size={11} weight="fill" />
                        Transfer Desk · {challenge.mode}
                    </div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight" data-testid="challenge-title">
                        {challenge.title}
                    </h1>
                    <p className="font-mono text-sm text-white/70 mt-2">
                        {challenge.shortDescription}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-3 font-mono text-[11px] uppercase tracking-widest text-white/50">
                        <span className="text-fa-cream">GW {dur.fromGameweek}–{dur.toGameweek}</span>
                        <span>· {challenge.players?.length || 0} players</span>
                        <span>· segments: {(challenge.segmentsUsed || []).join(", ")}</span>
                        {isTransferDesk && (
                            <span className="text-fa-yellow">
                                · constraints: {constraints.minBuy}× each
                            </span>
                        )}
                        {community?.total_attempts > 0 && (
                            <span className="text-fa-mint" data-testid="community-attempts">
                                · {community.total_attempts} manager{community.total_attempts === 1 ? "" : "s"} submitted
                            </span>
                        )}
                    </div>
                </header>

                {/* Briefing screen: scoring rules + Start */}
                {showBriefing && (
                    <BriefingScreen
                        challenge={challenge}
                        onStart={() => { setCardIndex(0); setGamePhase("playing"); }}
                        onGridMode={() => setGamePhase("grid")}
                    />
                )}

                {/* Playing screen: one card at a time */}
                {showPlaying && (
                    <CardModeGame
                        player={currentPlayer}
                        index={cardIndex}
                        total={totalCards}
                        pick={picks[String(currentPlayer.playerId)]}
                        confidence={confidences[String(currentPlayer.playerId)] ?? 50}
                        onConfidenceChange={(v) => handleCardConfidence(currentPlayer.playerId, v)}
                        onPick={handleCardPick}
                        onBack={() => goToCard(cardIndex - 1)}
                        onSkipToReview={() => setGamePhase("review")}
                        canGoBack={cardIndex > 0}
                    />
                )}

                {/* Review screen */}
                {showReview && (
                    <ReviewScreen
                        players={shuffledPlayers}
                        picks={picks}
                        confidences={confidences}
                        onEdit={(idx) => { setCardIndex(idx); setGamePhase("playing"); }}
                        onAllDone={() => setGamePhase("review")}
                    />
                )}

                {/* Counts row (visible during review + grid + after-submit) */}
                {(showReview || showGrid || submitted) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                    {ORDER.map((d) => {
                        const meta = DECISION_META[d];
                        const Icon = meta.icon;
                        const min = constraints[`min${d[0]}${d.slice(1).toLowerCase()}`];
                        return (
                            <div key={d} className="border border-white/10 bg-black/40 p-3" data-testid={`count-${d.toLowerCase()}`}>
                                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
                                    <Icon size={14} weight="bold" /> {d}
                                </div>
                                <div className="font-display font-black text-3xl mt-1 tabular-nums">
                                    {counts[d]}
                                    {min != null && (
                                        <span className="ml-2 font-mono text-xs text-white/40">
                                            / {min}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                )}

                {/* Player grid (legacy / power-user mode + after-submit reveal) */}
                {(showGrid || submitted) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="player-grid">
                    {(challenge.players || []).map((p) => (
                        <PlayerCard
                            key={p.playerId}
                            player={p}
                            pick={picks[String(p.playerId)] || picks[p.playerId]}
                            onPick={(d) => handlePick(p.playerId, d)}
                            disabled={submitted}
                            community={community?.players?.[String(p.playerId)]}
                            communityTotal={community?.total_attempts || 0}
                            revealCommunity={submitted}
                        />
                    ))}
                </div>
                )}

                {/* Guest identity (unauthed users, review/grid mode only) */}
                {!user && !submitted && (showReview || showGrid) && (
                    <div className="border border-fa-cream/30 bg-fa-cream/5 p-4" data-testid="guest-form">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2">
                            Hrát jako host · enter name to appear on leaderboard
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                    Display name <span className="text-fa-yellow">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    maxLength={30}
                                    placeholder="e.g. FantasyKing"
                                    className="w-full bg-black border border-white/10 rounded-none font-mono text-sm px-3 py-2 text-white focus:outline-none focus:border-fa-cream"
                                    data-testid="guest-name-input"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                    Email <span className="text-white/30 normal-case tracking-normal">(optional — for resolved-score notifications)</span>
                                </label>
                                <input
                                    type="email"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full bg-black border border-white/10 rounded-none font-mono text-sm px-3 py-2 text-white focus:outline-none focus:border-fa-cream"
                                    data-testid="guest-email-input"
                                />
                            </div>
                        </div>
                        <p className="font-mono text-[10px] text-white/40 mt-2">
                            Or <button type="button" onClick={() => window.location.assign("/")} className="text-fa-cream underline-offset-2 hover:underline">sign in with Google</button> for a permanent profile + reputation rep.
                        </p>
                    </div>
                )}

                {/* Submit / status row (visible in review/grid + post-submit) */}
                {(showReview || showGrid || submitted) && (
                <div className="border border-white/10 bg-black/40 p-4 sticky bottom-3 backdrop-blur-md flex items-center justify-between gap-3 flex-wrap" data-testid="submit-row">
                    <div className="font-mono text-[11px] text-white/60">
                        {submitted ? (
                            <span className="text-fa-mint flex items-center gap-1.5">
                                <CheckCircle size={12} weight="fill" />
                                Submitted · graded after GW{dur.toGameweek}
                            </span>
                        ) : constraintsMessage && constraintsMessage.length > 0 ? (
                            <span className="text-amber-400">{constraintsMessage[0]}</span>
                        ) : (
                            <span className="text-fa-mint">Ready to lock in {!user && guestName.trim() ? `as ${guestName.trim()}` : ""}</span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitted || submitting || (constraintsMessage && constraintsMessage.length > 0)}
                        data-testid="submit-decisions"
                        className="px-5 py-2 bg-fa-yellow text-black font-display font-black uppercase tracking-wider text-sm hover:bg-fa-yellow/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        {submitted ? "Locked in" : submitting ? "Submitting…" : "Lock in picks"}
                    </button>
                </div>
                )}

                {/* Leaderboard preview after submit */}
                {submitted && (
                    <>
                        {resolution && (
                            <BeatTheModelPanel
                                resolution={resolution}
                                cid={cid}
                                userId={user?.user_id || guestUserId}
                            />
                        )}
                        {summary && <MarketCallSummaryPanel summary={summary} />}
                        <LeaderboardWidget cid={cid} myUserId={user?.user_id || guestUserId} />
                        {(user?.user_id || guestUserId) && (
                            <ArchetypePanel userId={user?.user_id || guestUserId} />
                        )}
                    </>
                )}
            </div>
        </AppShell>
    );
}

function PlayerCard({ player, pick, onPick, disabled, community, communityTotal, revealCommunity }) {
    const tagsLine = (player.tags || []).slice(0, 4).join(" · ");
    const showBar = revealCommunity && community && community.total >= 1;
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid={`player-card-${player.playerId}`}>
            <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display font-black text-lg truncate">{player.webName}</div>
                    <div className="font-mono text-[11px] text-white/50">
                        {player.display?.subtitle}
                    </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream flex-shrink-0" data-testid={`market-label-${player.playerId}`}>
                    {player.display?.marketLabel}
                </div>
            </div>
            <div className="font-mono text-[11px] text-white/60 mt-2">
                {player.display?.shortReason}
            </div>
            {tagsLine && (
                <div className="font-mono text-[10px] text-white/40 mt-1 truncate">{tagsLine}</div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-3" data-testid={`decision-row-${player.playerId}`}>
                {ORDER.map((d) => {
                    const meta = DECISION_META[d];
                    const Icon = meta.icon;
                    const active = pick === d;
                    return (
                        <button
                            key={d}
                            type="button"
                            onClick={() => onPick(d)}
                            disabled={disabled}
                            data-testid={`decide-${player.playerId}-${d.toLowerCase()}`}
                            className={`flex items-center justify-center gap-1 py-2 px-1 font-mono text-[10px] uppercase tracking-widest border transition-colors ${
                                active
                                    ? "text-black"
                                    : "text-white/60 border-white/10 hover:border-white/40 hover:text-white"
                            } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                            style={active ? { background: meta.color, borderColor: meta.color } : undefined}
                        >
                            <Icon size={10} weight="bold" /> {d}
                        </button>
                    );
                })}
            </div>

            {/* Community heatmap — revealed after user submits to avoid bias */}
            {showBar ? (
                <CommunityBar
                    playerId={player.playerId}
                    counts={community}
                    userPick={pick}
                />
            ) : (!revealCommunity && community?.total >= 3) ? (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-3" data-testid={`community-locked-${player.playerId}`}>
                    Community: {community.total} pick{community.total === 1 ? "" : "s"} hidden until you lock in
                </div>
            ) : null}

            {/* Beat-the-Model hint, hidden until user picks */}
            {pick && player.modelRecommendation && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-2 flex items-center gap-1.5">
                    <Trophy size={10} weight="fill" />
                    Model says: <span style={{ color: DECISION_META[player.modelRecommendation]?.color }}>
                        {player.modelRecommendation}
                    </span>
                    {pick !== player.modelRecommendation && (
                        <span className="text-fa-yellow ml-1">· you went rogue</span>
                    )}
                </div>
            )}
        </div>
    );
}

function CommunityBar({ playerId, counts, userPick }) {
    const total = counts.total || 0;
    if (!total) return null;
    return (
        <div className="mt-3 space-y-1.5" data-testid={`community-bar-${playerId}`}>
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/40">
                <span>Community ({total})</span>
                {counts.consensus && (
                    <span style={{ color: DECISION_META[counts.consensus]?.color }} data-testid={`community-consensus-${playerId}`}>
                        Consensus: {counts.consensus}
                    </span>
                )}
            </div>
            <div className="flex h-2 w-full overflow-hidden bg-black/40 border border-white/5">
                {ORDER.map((d) => {
                    const pct = (counts[d] / total) * 100;
                    if (pct <= 0) return null;
                    return (
                        <div
                            key={d}
                            style={{ width: `${pct}%`, background: DECISION_META[d].color }}
                            data-testid={`community-seg-${playerId}-${d.toLowerCase()}`}
                            title={`${d}: ${counts[d]} (${pct.toFixed(0)}%)`}
                        />
                    );
                })}
            </div>
            <div className="grid grid-cols-4 gap-1 font-mono text-[10px] tabular-nums">
                {ORDER.map((d) => {
                    const pct = total ? (counts[d] / total) * 100 : 0;
                    const mine = userPick === d;
                    return (
                        <div
                            key={d}
                            className={`flex items-center justify-between px-1 py-0.5 border ${mine ? "border-white/40" : "border-white/5"}`}
                            data-testid={`community-cell-${playerId}-${d.toLowerCase()}`}
                        >
                            <span style={{ color: DECISION_META[d].color }}>{d[0]}</span>
                            <span className="text-white/70">{pct.toFixed(0)}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function LeaderboardWidget({ cid, myUserId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        getChallengeLeaderboard(cid, 50)
            .then((d) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData({ items: [], resolved_count: 0, pending_count: 0 }); });
        return () => { cancelled = true; };
    }, [cid]);
    if (!data) return null;
    const items = data.items || [];
    return (
        <section className="border border-white/10 bg-[#0d0d0d] p-5" data-testid="leaderboard-widget">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                        <Trophy size={11} weight="fill" />
                        Leaderboard
                    </div>
                    <h3 className="font-display font-black text-lg uppercase tracking-tight mt-1">
                        {data.resolved_count > 0 ? `Top ${data.resolved_count} after grading` : `${data.pending_count} managers waiting on grading`}
                    </h3>
                </div>
                <span className="font-mono text-[10px] text-white/40">
                    {data.resolved_count} ranked · {data.pending_count} pending
                </span>
            </div>
            {items.length === 0 ? (
                <div className="font-mono text-sm text-white/40 py-6 text-center">No submissions yet.</div>
            ) : (
                <div className="divide-y divide-white/5" data-testid="leaderboard-rows">
                    {items.map((it, idx) => {
                        const mine = myUserId && it.user_id === myUserId;
                        return (
                            <div
                                key={`${it.user_id}-${idx}`}
                                className={`flex items-center justify-between gap-3 py-2 ${mine ? "bg-fa-cream/10 -mx-3 px-3" : ""}`}
                                data-testid={`leaderboard-row-${idx}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="font-mono text-[11px] text-white/40 w-6 tabular-nums">
                                        {it.rank ? `#${it.rank}` : "—"}
                                    </span>
                                    <span className="font-mono text-sm text-white truncate">
                                        {it.name}
                                        {it.is_guest && (
                                            <span className="ml-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40">guest</span>
                                        )}
                                        {mine && (
                                            <span className="ml-1.5 font-mono text-[9px] uppercase tracking-widest text-fa-yellow">you</span>
                                        )}
                                    </span>
                                </div>
                                <div className="font-mono text-[11px] tabular-nums">
                                    {it.status === "resolved" ? (
                                        <span className="text-fa-mint">+{it.total_score} FAP</span>
                                    ) : (
                                        <span className="text-white/40">pending</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}


function BeatTheModelPanel({ resolution, cid, userId }) {
    const breakdown = resolution.breakdown || [];
    const totalPlayers = breakdown.length;
    const beat = resolution.beat_model_count || 0;
    const matched = resolution.matched_model_count || 0;
    const total = beat + matched;
    return (
        <section className="border border-fa-violet/30 bg-gradient-to-br from-fa-violet/15 to-[#0d0d0d] p-5 space-y-4" data-testid="beat-the-model-panel">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                        <Trophy size={11} weight="fill" />
                        Resolved · GW {resolution.from_gw}
                        {resolution.to_gw !== resolution.from_gw ? `–${resolution.to_gw}` : ""}
                    </div>
                    <h3 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                        Beat the Model
                    </h3>
                </div>
                <div className="text-right">
                    <div className="font-display font-black text-3xl text-fa-mint tabular-nums" data-testid="resolution-total-fap">
                        +{resolution.total_score} FAP
                    </div>
                    {resolution.rep_awarded > 0 && (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mt-0.5">
                            +{resolution.rep_awarded} reputation bonus
                        </div>
                    )}
                </div>
            </div>

            {/* Beat-the-model summary bar */}
            {total > 0 && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                        <span className="text-fa-mint">Went rogue · {beat} {total === 1 ? "win" : "wins"}</span>
                        <span className="text-white/40">Matched model · {matched}</span>
                    </div>
                    <div className="flex h-2 w-full overflow-hidden bg-black/40 border border-white/5">
                        {beat > 0 && (
                            <div style={{ width: `${(beat / total) * 100}%` }} className="bg-fa-mint" data-testid="bar-beat" />
                        )}
                        {matched > 0 && (
                            <div style={{ width: `${(matched / total) * 100}%` }} className="bg-white/30" data-testid="bar-matched" />
                        )}
                    </div>
                </div>
            )}

            {/* Per-player breakdown */}
            <div className="divide-y divide-white/5" data-testid="resolution-breakdown">
                {breakdown.map((row) => (
                    <PlayerBreakdownRow key={row.player_id} row={row} />
                ))}
            </div>
            <div className="font-mono text-[10px] text-white/40 text-center pt-2">
                {totalPlayers} player{totalPlayers === 1 ? "" : "s"} graded · scoring rules per Fantasy Academy spec
            </div>
            {cid && userId && <ShareCardRow cid={cid} userId={userId} />}
        </section>
    );
}

function PlayerBreakdownRow({ row }) {
    const userMeta = DECISION_META[row.decision];
    const modelMeta = row.model_recommendation ? DECISION_META[row.model_recommendation] : null;
    const beatModel = modelMeta && row.decision !== row.model_recommendation && row.score > 0;
    const conf = row.confidence;
    const confMul = row.confidence_multiplier;
    return (
        <div className="py-2.5" data-testid={`breakdown-${row.player_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="font-display font-black text-sm truncate">{row.name}</span>
                    {beatModel && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-fa-mint border border-fa-mint/40 px-1.5 py-0.5">
                            BEAT MODEL
                        </span>
                    )}
                    {conf != null && conf !== 50 && (
                        <span
                            className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${conf >= 75 ? "text-fa-yellow border-fa-yellow/40" : "text-white/40 border-white/20"}`}
                            data-testid={`breakdown-conf-${row.player_id}`}
                        >
                            {conf}% · {confMul}×
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest">
                    <span style={{ color: userMeta?.color || "#fff" }}>You: {row.decision}</span>
                    {modelMeta && (
                        <span className="text-white/40">
                            Model: <span style={{ color: modelMeta.color }}>{row.model_recommendation}</span>
                        </span>
                    )}
                    <span className={`tabular-nums ${row.score > 0 ? "text-fa-mint" : "text-white/30"}`}>
                        {row.score > 0 ? `+${row.score}` : "0"} FAP
                    </span>
                </div>
            </div>
            {row.components && row.components.length > 0 && (
                <div className="font-mono text-[10px] text-white/50 mt-1 pl-1">
                    {row.components.join(" · ")}
                </div>
            )}
            {row.stats && (
                <div className="font-mono text-[10px] text-white/30 mt-0.5 pl-1">
                    Actual: {row.stats.points} pts · {row.stats.minutes}min · played {row.stats.played}/{row.stats.total_games}
                </div>
            )}
        </div>
    );
}


function MarketCallSummaryPanel({ summary }) {
    const best = summary.best_decision;
    const worst = summary.worst_trap;
    const insights = summary.insights || [];
    const topUsers = summary.top_users || [];
    return (
        <section className="border border-fa-cream/30 bg-[#0d0d0d] p-5 space-y-4" data-testid="market-call-summary">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5">
                        <Trophy size={11} weight="fill" />
                        Market Call · Evaluated
                    </div>
                    <h3 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                        Challenge Summary
                    </h3>
                </div>
                <div className="font-mono text-[11px] text-white/40">
                    {summary.total_managers} manager{summary.total_managers === 1 ? "" : "s"} graded
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {best && (
                    <div className="border border-fa-mint/30 bg-fa-mint/5 p-3" data-testid="summary-best-decision">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint mb-1">
                            ✓ Best decision
                        </div>
                        <div className="font-display font-black text-lg">
                            {best.player_name}
                        </div>
                        <div className="font-mono text-[11px] text-white/60 mt-1">
                            Right call: <span style={{ color: DECISION_META[best.best_call]?.color }}>{best.best_call}</span>
                            {" · "}outcome <span className="text-white/80">{best.outcome}</span>
                        </div>
                        <div className="font-mono text-[10px] text-white/40 mt-1">
                            {best.consensus_count} manager{best.consensus_count === 1 ? "" : "s"} called it correctly
                        </div>
                    </div>
                )}
                {worst && (
                    <div className="border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 p-3" data-testid="summary-worst-trap">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF6B6B] mb-1">
                            ✗ Worst trap
                        </div>
                        <div className="font-display font-black text-lg">
                            {worst.player_name}
                        </div>
                        <div className="font-mono text-[11px] text-white/60 mt-1">
                            Most chose <span style={{ color: DECISION_META[worst.wrong_popular_call]?.color }}>{worst.wrong_popular_call}</span>
                            {" · "}actual <span className="text-white/80">{worst.outcome}</span>
                        </div>
                        <div className="font-mono text-[10px] text-white/40 mt-1">
                            {worst.consensus_count} manager{worst.consensus_count === 1 ? "" : "s"} were caught out
                        </div>
                    </div>
                )}
            </div>

            {insights.length > 0 && (
                <div data-testid="summary-insights">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream mb-2">
                        Market insights
                    </div>
                    <ul className="space-y-1">
                        {insights.map((line, i) => (
                            <li key={i} className="font-mono text-[12px] text-white/80 leading-snug pl-2 border-l border-fa-cream/30">
                                {line}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {topUsers.length > 0 && (
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-2 flex items-center gap-1.5">
                        <Trophy size={10} weight="fill" /> Top advisors
                    </div>
                    <div className="space-y-1" data-testid="summary-top-users">
                        {topUsers.map((u, idx) => (
                            <div key={u.user_id} className="flex items-center justify-between font-mono text-[11px] py-1 border-b border-white/5 last:border-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-white/40 tabular-nums">#{idx + 1}</span>
                                    <span className="text-white">{u.display_name}</span>
                                    {u.is_guest && <span className="text-[9px] uppercase tracking-widest text-white/40">guest</span>}
                                </div>
                                <span className="text-fa-mint tabular-nums">+{u.total_fap} FAP</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

function ShareCardRow({ cid, userId }) {
    const url = buildShareCardUrl(cid, userId);
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Image URL copied — paste it into your X / Twitter post.");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Couldn't copy — long-press the image to save it manually.");
        }
    };
    const handleTweet = () => {
        const text = encodeURIComponent("My Fantasy Academy Transfer Desk result 👇");
        const tweetUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`;
        window.open(tweetUrl, "_blank", "noopener,noreferrer");
    };
    return (
        <div className="border-t border-white/10 pt-4 mt-4 space-y-3" data-testid="share-card-row">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow mb-2 flex items-center gap-1.5">
                <Trophy size={10} weight="fill" />
                Share your result
            </div>
            <div className="border border-white/10 overflow-hidden bg-black">
                <img
                    src={url}
                    alt="Fantasy Academy share card"
                    className="block w-full h-auto"
                    data-testid="share-card-image"
                    loading="lazy"
                />
            </div>
            <div className="flex gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={handleTweet}
                    data-testid="share-tweet-btn"
                    className="flex-1 min-w-[160px] bg-fa-yellow text-black font-display font-black uppercase tracking-wide px-4 py-2.5 hover:bg-fa-yellow/90 transition-colors"
                >
                    Post on X →
                </button>
                <button
                    type="button"
                    onClick={handleCopy}
                    data-testid="share-copy-btn"
                    className="flex-1 min-w-[160px] border border-white/30 text-white font-display font-black uppercase tracking-wide px-4 py-2.5 hover:border-white transition-colors"
                >
                    {copied ? "Copied!" : "Copy image URL"}
                </button>
            </div>
        </div>
    );
}

function ArchetypePanel({ userId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        getUserArchetype(userId)
            .then((d) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); });
        return () => { cancelled = true; };
    }, [userId]);
    if (!data) return null;
    const isRookie = data.archetype === "ROOKIE";
    const stats = data.stats || {};
    return (
        <section
            className="border-2 p-5 space-y-3"
            style={{ borderColor: data.color || "#888" }}
            data-testid="archetype-panel"
        >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: data.color || "#888" }}>
                        Your Manager Archetype
                    </div>
                    <h3 className="font-display font-black text-2xl uppercase tracking-tight mt-1" data-testid="archetype-label">
                        {data.label}
                    </h3>
                    <p className="font-mono text-[11px] text-white/60 mt-1">
                        {data.tagline}
                    </p>
                </div>
                <div className="font-mono text-[11px] text-white/40">
                    {stats.challenges_played} challenge{stats.challenges_played === 1 ? "" : "s"} played
                </div>
            </div>
            {!isRookie && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-white/10">
                    <ArchetypeStat label="Total FAP" value={`+${stats.total_fap || 0}`} color="text-fa-mint" />
                    <ArchetypeStat label="Model accuracy" value={`${stats.model_accuracy_pct || 0}%`} />
                    <ArchetypeStat label="Contrarian wins" value={stats.contrarian_wins || 0} />
                    <ArchetypeStat label="Hidden gem picks" value={stats.hidden_gem_picks || 0} />
                </div>
            )}
            {data.secondary?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">also:</span>
                    {data.secondary.map((s) => (
                        <span
                            key={s.archetype}
                            className="font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5"
                            style={{ borderColor: s.color, color: s.color }}
                            data-testid={`archetype-secondary-${s.archetype}`}
                        >
                            {s.label}
                        </span>
                    ))}
                </div>
            )}
        </section>
    );
}

function ArchetypeStat({ label, value, color = "text-white" }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</div>
            <div className={`font-display font-black text-xl ${color}`}>{value}</div>
        </div>
    );
}

